import React, { useMemo, useState } from 'react';
import {
  PhotoUploadInput,
  PhotoSelectFromMulti,
  DataField,
  ReferenceEditor,
  FieldInlineEdit,
} from '../index';
import { Button, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const defaultContact = {
  method: 'email',
  value: '',
  visibility: 'public',
};

const contactMethodOptions = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'form', label: 'Form' },
];

const LinkCandidateProfileEditor = ({
  value,
  onChange,
  race,
  candidateId,
  electionKey,
  photoOptions,
  disabled,
  stateName = null,
  showFullPageLink = true,
}) => {
  const contact = useMemo(() => value?.contact || [], [value?.contact]);
  const [editingName, setEditingName] = useState(false);

  const setContact = (newContact) => {
    onChange({
      ...value,
      contact: newContact,
    });
  };

  const updateContact = (index, patch) => {
    setContact(
      contact.map((item, i) => (i === index ? { ...item, ...patch } : item))
    );
  };

  const addContact = () => {
    setContact([...contact, defaultContact]);
  };

  const removeContact = (index) => {
    setContact(contact.filter((_, i) => i !== index));
  };

  return (
    <Wrapper>
      <WrapperInner>
        {stateName && (
          <Typography variant='h2'>State: {stateName}</Typography>
        )}
        <Header>
          <PhotoUploadInput
            style={{
              margin: '0 0 16px',
              alignSelf: 'center',
              maxWidth: '700px',
            }}
            value={value?.photoPathFace}
            onChange={(photoPathFace) => onChange({ ...value, photoPathFace })}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {editingName ? (
              <FieldInlineEdit
                inputComponent={
                  <TextField
                    value={value?.name}
                    name={'name'}
                    disabled={disabled}
                    onChange={(e) => onChange({ ...value, name: e.target.value })}
                    style={{ width: 'calc(100% - 15px)' }}
                  />
                }
                onCancel={() => setEditingName(false)}
                onSave={() => setEditingName(false)}
              />
            ) : (
              <DataField onEdit={disabled ? null : () => setEditingName(true)}>
                <Typography variant='h3'>{value?.name}</Typography>
              </DataField>
            )}
            <Typography variant='body1'>
              Running for {race?.officeName || race?.office?.name} in {race?.district?.longName}.
            </Typography>
            {showFullPageLink && (
              <Link to={`/elections/${electionKey}/races/${race?._id}/candidates/${candidateId}`} target='_blank'>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant='body2'>Full page</Typography>
                  <OpenInNewIcon style={{ width: '15px', height: '15px' }} />
                </div>
              </Link>
            )}
          </div>
        </Header>

        {(photoOptions || []).length > 0 && (
          <div style={{ margin: '0 24px 0' }}>
            <PhotoSelectFromMulti
              size={value?.photoPathFace ? 'small' : 'medium'}
              photoOptions={photoOptions}
              onSelect={(photoPathFace) => onChange({ ...value, photoPathFace })}
            />
          </div>
        )}

        <Section>
          <ReferenceEditor
            references={value?.references}
            onChangeActiveDraft={(update) =>
              onChange({
                ...value,
                ...update,
              })
            }
            activeDraft={value}
            disabled={disabled}
            categoryTypesToRender={['website', 'social']}
            showSourceMetadata={false}
          />
        </Section>

        <Section>
          <Typography variant='body1' style={{ fontWeight: 'bold' }}>Contact information</Typography>
          {(contact || []).length === 0 ? (
            <Typography variant='body2'>No contact information added yet.</Typography>
          ) : (
            <ContactList>
              {contact.map((item, index) => (
                <ContactRow key={`contact-${index}`}>
                  <TextField
                    select
                    size='small'
                    value={item?.method || 'email'}
                    onChange={(e) => updateContact(index, { method: e.target.value })}
                    style={{ width: '180px' }}
                  >
                    {contactMethodOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size='small'
                    value={item?.value || ''}
                    onChange={(e) => updateContact(index, { value: e.target.value })}
                    placeholder='Enter contact value'
                    style={{ flex: 1 }}
                  />
                  <IconButton onClick={() => removeContact(index)} size='small'>
                    <DeleteIcon />
                  </IconButton>
                </ContactRow>
              ))}
            </ContactList>
          )}
          <Button onClick={addContact} variant='outlined' size='small'>
            Add contact
          </Button>
        </Section>
      </WrapperInner>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 36px;
`;

const WrapperInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;
  max-width: 760px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export { LinkCandidateProfileEditor };
